import { useEffect, useState } from "react";
// 组件
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { ProButton, ProModal, } from '../../antd';
// 方法
import { formatRowKey } from "../util";
import { renderEnumNames } from "../../utils/renderUtils";
import { dispatchFunc } from "./useEditSubTablehook";
import { getColumnValue } from "../../utils/dataUtils";
import { filterEmptyValue } from "../../utils/utils";
// 常量
import { ADD_CHILDREN_KEY, EditMainSubTableColumnType, EditMainSubTablePrope, EMPTY_CHILDREN_KEY, MAIN_ROW_KEY, mainFormKey, } from "../interface";
import { IEnum } from "../../PageLayout/context/LayoutContext";
import { FormInstance } from "antd/lib/form";

function columnsHook(
  props: EditMainSubTablePrope,
  dispatchEdiTable: dispatchFunc,
  tableDataSource?: any[],
  enumData?: IEnum,
  form?: FormInstance,
) {
  const {
    columns, hideAdd, addIcon, operation, disabled, deleteType, operationTitle, addSubRowText, emptySubRowText, operationWidth,
    rowRender, onAdd, onAddChildren, onDelete, disabledOperation,
  } = props;
  const [tableColumns, setTableColumns] = useState<EditMainSubTableColumnType[]>();
  useEffect(() => {
    const newTableColumns = formatColumns(columns);
    setTableColumns(newTableColumns);
  }, [columns, operation, operationTitle, disabled, tableDataSource]);
  /** 添加 */
  const handleAdd = () => {
    const addItem = onAdd?.() ?? {};
    dispatchEdiTable('addRow', { addItem });
  };
  /** 添加子行 */
  const handleChildrenAdd = (record: any) => {
    const { mainKey } = formatRowKey(record.key);
    const addItem = onAddChildren ? onAddChildren({
      ...(tableDataSource?.find(item => item.key === mainKey)),
      ...(mainKey ? form?.getFieldsValue([mainKey, mainFormKey,]) : undefined),
    }) : {};
    if (!addItem) return;
    dispatchEdiTable('addChildren', { mainKey, addItem });
  };
  /** 删除主行 */
  const handleMainDelete = (record: any, index: number) => {
    let effectiveRow: any[] = [];
    const children = record.children;
    if (Array.isArray(children)) {
      effectiveRow = children.filter(v => !v[ADD_CHILDREN_KEY] && !v[EMPTY_CHILDREN_KEY]);
    }
    const newRecord = { ...record, children: effectiveRow, };
    if (onDelete) {
      const result = onDelete(newRecord, index);
      if (result === false) return;
      dispatchEdiTable('deleteRow', { mainKey: record.key });
    } else {
      if (effectiveRow.length) {
        ProModal.confirm({
          title: `确认删除该行数据和${effectiveRow.length}条子表数据？`,
          okText: '确定',
          cancelText: '取消',
          onOk: () => {
            dispatchEdiTable('deleteRow', { mainKey: record.key });
          },
        });
      } else {
        dispatchEdiTable('deleteRow', { mainKey: record.key });
      }
    }
  };
  /** 删除子行 */
  const handleDelete = (record: any, index: number) => {
    const { mainKey } = formatRowKey(record.key);
    const result = onDelete?.(record, Number(mainKey), index);
    if (result === false) return;
    dispatchEdiTable('deleteChildren', { mainKey, childrenKey: record.key });
  };
  /** 格式化列配置 */
  function formatColumns(columnsProps?: EditMainSubTableColumnType[]): EditMainSubTableColumnType[] {
    let newTableColumns: EditMainSubTableColumnType[] = [];
    if (columnsProps && columnsProps.length) {
      const columnsLength = columnsProps.length;
      const totalLength = columnsProps.length + ((operation !== false && !disabled) ? 1 : 0);
      newTableColumns = columnsProps.map((column, index) => {
        const { editable, formItemType, disabled: columnDisabled, enumCode, disabledRender, title, dataIndex, width, align, fixed, ...propsColumn } = column;
        const baseColumn: EditMainSubTableColumnType = {
          title,
          dataIndex,
          width,
          align,
          fixed,
          // shouldCellUpdate: (record: any, prevRecord: any) => {
          //   if (record[MAIN_ROW_KEY] && prevRecord[MAIN_ROW_KEY]) return false;
          //   return true;
          // },
        };
        if (editable) {
          return {
            ...baseColumn,
            onCell:  (record: any, rowIndex: any): any => {
              if (record[MAIN_ROW_KEY]) {
                return {
                  record,
                  colSpan: index === 0 ? columnsLength : 0,
                  className: 'yb-table-row-main',
                }
              }
              if (record[ADD_CHILDREN_KEY]) {
                return {
                  record,
                  colSpan: index === 0 ? totalLength : 0,
                  className: 'yb-table-row-more text-align-center',
                }
              }
              if (record[EMPTY_CHILDREN_KEY]) {
                return {
                  record,
                  colSpan: index === 0 ? totalLength : 0,
                  className: 'yb-table-row-more text-align-center',
                }
              }
              let initialValue: any;
              if (typeof dataIndex === 'string') initialValue = record[dataIndex];
              if (
                formItemType === 'SelectTable' &&
                Array.isArray(dataIndex)
              ) {
                initialValue = record[dataIndex[0]];
              }
              if (!initialValue && propsColumn.initialValue !== undefined)
                initialValue = propsColumn.initialValue;
              return {
                record,
                editable: true,
                disabled: disabled || columnDisabled,
                disabledRender,
                formItemType,
                rowIndex,
                form,
                SimpleFormItemProps: {
                  label: title,
                  name: dataIndex,
                  initialValue,
                  ...propsColumn
                },
              };
            },
            render: (val: any, record: any) => {
              if (record[MAIN_ROW_KEY]) {
                if (index === 0) return rowRender?.(record);
                return null;
              }
              if (record[ADD_CHILDREN_KEY]) {
                if (index === 0) {
                  return (
                    <div
                      className="move-pointer"
                      onClick={() => handleChildrenAdd(record)}
                    >
                      <PlusOutlined />
                      <span>{addSubRowText}</span>
                    </div>
                  );
                }
                return null;
              }
              if (record[EMPTY_CHILDREN_KEY]) {
                if (index === 0) {
                  return (
                    <div>{emptySubRowText}</div>
                  );
                }
                return null;
              }
              return val;
            },
          };
        }
        if (enumCode && enumData) {
          const enumItem = enumData[`${enumCode}`];
          return enumItem
            ? {
                ...baseColumn,
                ...column,
                onCell: (record: any) => {
                  if (record[MAIN_ROW_KEY]) {
                    return {
                      record,
                      colSpan: index === 0 ? columnsLength : 0,
                      className: 'yb-table-row-main',
                    };                          
                  }
                  if (record[ADD_CHILDREN_KEY]) {
                    return {
                      record,
                      colSpan: index === 0 ? totalLength : 0,
                      className: 'yb-table-row-more text-align-center',
                    }
                  }
                  if (record[EMPTY_CHILDREN_KEY]) {
                    return {
                      record,
                      colSpan: index === 0 ? totalLength : 0,
                      className: 'yb-table-row-empty text-align-center',
                    }
                  }
                },
                render: (val: any, record: any) => {
                  const columnValue = getColumnValue(record, dataIndex);
                  return renderEnumNames(columnValue, enumItem.items);
                },
              }
            : { ...baseColumn, ...column };
        }
        return {
          ...baseColumn,
          onCell: (record: any) => {
            if (record[MAIN_ROW_KEY]) {
              return {
                record,
                colSpan: index === 0 ? columnsLength : 0,
                className: 'yb-table-row-main',
              };                          
            }
            if (record[ADD_CHILDREN_KEY]) {
              return {
                record,
                colSpan: index === 0 ? totalLength : 0,
                className: 'yb-table-row-more text-align-center',
              }
            }
            if (record[EMPTY_CHILDREN_KEY]) {
              return {
                record,
                colSpan: index === 0 ? totalLength : 0,
                className: 'yb-table-row-empty text-align-center',
              }
            }
          },
          render: (_: any, record: any) => {
            const columnValue = getColumnValue(record, dataIndex);
            if (record[MAIN_ROW_KEY]) {
              if (index === 0) return rowRender?.(record);
              return null;
            }
            if (record[ADD_CHILDREN_KEY]) {
              if (index === 0) {
                return (
                  <div
                    className="move-pointer"
                    onClick={() => handleChildrenAdd(record)}
                  >
                    <PlusOutlined />
                    <span>{addSubRowText}</span>
                  </div>
                );
              }
              return null;
            }
            if (record[EMPTY_CHILDREN_KEY]) {
              if (index === 0) {
                return (
                  <div>{emptySubRowText}</div>
                );
              }
              return null;
            }
            return filterEmptyValue(columnValue);
          },
        };
      });
      newTableColumns
    }
    if (operation !== false && !disabled) {
      // @ts-ignore
      const operationColumns: EditMainSubTableColumnType = {
        fixed: 'right',
        width: operationWidth,
        align: 'center',
        title: hideAdd ? null : (operationTitle ? operationTitle : (
          <ProButton type="primary" size="small" onClick={handleAdd}>
            {addIcon}
          </ProButton>
        )),
        onCell: (record: any) => {
          if (record[MAIN_ROW_KEY]) {
            return {
              className: 'yb-table-row-main',
            }
          }
          if (record[ADD_CHILDREN_KEY]) {
            return {
              colSpan: 0,
            }
          }
          if (record[EMPTY_CHILDREN_KEY]) {
            return {
              colSpan: 0,
            }
          }
          return {};
        },
        render: (val: any, record: any, key: number) => {
          if (record[MAIN_ROW_KEY]) {
            if (deleteType === 'CHILDREN') return null;
            if (disabledOperation) {
              const flag = disabledOperation(record);
              if (flag) return null;
            }
            return (
              <DeleteOutlined
                className='text-danger'
                onClick={() => handleMainDelete(record, key)}
                title="删除"
              />
            );
          }
          if (deleteType === 'MAIN') return null;
          if (disabledOperation) {
            const flag = disabledOperation(record);
            if (flag) return null;
          }
          return (
            <DeleteOutlined
              className='text-danger'
              onClick={() => handleDelete(record, key)}
              title="删除"
            />
          );
        },
        ...operation,
      };
      newTableColumns.push(operationColumns);
    }
    return newTableColumns;
  }
  return {
    tableColumns,
  };
}

export default columnsHook;